import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  Typography,
  makeStyles,
} from "@material-ui/core";

import { GenerateTicketsProgressDialogProps } from "../../../interface/dialog/GenerateTicketsProgressDialogProps";
import { useDialogStyles } from "./styles";
import { bPrefix, formatTicketNumber } from "../../../utils/parser";

// The shared dialogContent leaves the bottom padding to DialogActions. This dialog has
// no actions — it closes itself when the request returns — so it supplies its own.
const useStyles = makeStyles((theme) => ({
  content: {
    paddingBottom: theme.spacing(3),
  },
  progress: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
}));

// Generation is a single transactional request, so the server reports no incremental
// progress — the bar is deliberately indeterminate rather than faking a percentage.
// The elapsed timer is what tells the operator it is still working.
const GenerateTicketsProgressDialog = ({
  open,
  from,
  to,
  isBSeries,
}: GenerateTicketsProgressDialogProps) => {
  const classes = useDialogStyles();
  const localClasses = useStyles();
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!open) {
      setElapsed(0);
      return;
    }
    const started = Date.now();
    const timer = setInterval(
      () => setElapsed(Math.floor((Date.now() - started) / 1000)),
      1000
    );
    return () => clearInterval(timer);
  }, [open]);

  const count = to - from + 1;
  const prefix = bPrefix({ isBSeries });
  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  const elapsedLabel = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

  return (
    // Not dismissible: closing the dialog would not cancel the request, and the tab
    // must stay open until it returns.
    <Dialog open={open} maxWidth="sm" fullWidth disableBackdropClick disableEscapeKeyDown>
      <DialogTitle className={classes.dialogTitle}>
        Generating Tickets
      </DialogTitle>
      <DialogContent
        className={`${classes.dialogContent} ${localClasses.content}`}
      >
        <div className={classes.stackedContent}>
          <Typography>
            Generating <b>{count.toLocaleString()}</b> tickets, numbered{" "}
            <b>
              {prefix}
              {formatTicketNumber(from)}
            </b>{" "}
            to{" "}
            <b>
              {prefix}
              {formatTicketNumber(to)}
            </b>
            .
          </Typography>
          <LinearProgress className={localClasses.progress} />
          <Typography className={classes.helperText}>
            {`${elapsedLabel} elapsed. Large batches can take a few minutes — please keep this tab open.`}
          </Typography>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateTicketsProgressDialog;
